import {
  GridActionsCellItem,
  type GridGetRowsToExportParams,
  type GridRenderCellParams,
  type GridRowParams,
  type GridValidRowModel,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  isAutogeneratedRow,
} from '@mui/x-data-grid-premium';
import { RiCheckLine, RiCloseLine, RiFileCopyLine, RiPencilLine, RiToggleFill, RiToggleLine } from '@remixicon/react';
import { type ReactNode, useMemo } from 'react';
import * as Countries from 'shared/constants/Countries';
import money from 'shared/utils/Money';
import BoldHeader from 'src/components/atoms/grid/BoldHeader';
import { type GridColObj, getDistinctValueOptions } from 'src/modules/grids';
import { COMMODITY_KEY_PREFIX, isCommodityRow } from '../catalogDataSource';
import type { CatalogGridRow } from './useCommoditiesPage';

/** A commodity header row: a real server row now, not an auto-generated group. It has no product fields. */
const isHeaderRow = (row: GridValidRowModel) => isCommodityRow(row);

const OptionalRenderCell = ({
  formattedValue,
  rowNode,
  row,
}: GridRenderCellParams<CatalogGridRow, unknown, ReactNode>) => {
  if (rowNode.depth === 0 || isAutogeneratedRow(row) || isHeaderRow(row)) {
    return null;
  }

  return <>{formattedValue ?? '--'}</>;
};

// For boolean columns, hide for parent/empty rows and render check/close icons for data rows
const OptionalBooleanRenderCell = ({ rowNode, row, value }: GridRenderCellParams<CatalogGridRow, boolean | null>) => {
  if (rowNode.depth === 0 || isAutogeneratedRow(row) || isHeaderRow(row)) {
    return null;
  }

  if (value === true) {
    return <RiCheckLine size={20} />;
  }
  if (value === false) {
    return <RiCloseLine size={20} />;
  }
  return null;
};

type UseCommoditiesColumnsProps = {
  customFields: GridColObj<CatalogGridRow> | null;
  onEdit?: (product: CatalogGridRow) => void;
  onDuplicate?: (product: CatalogGridRow) => void;
  onToggleDisabled?: (product: CatalogGridRow) => void;
};

export function useCommoditiesColumns({
  customFields,
  onEdit,
  onDuplicate,
  onToggleDisabled,
}: UseCommoditiesColumnsProps): GridColObj<CatalogGridRow> {
  return useMemo(
    (): GridColObj<CatalogGridRow> => ({
      productCode: {
        headerName: 'Product Code',
        type: 'string',
        minWidth: 150,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
      },
      commodityStyle: {
        headerName: 'Variety',
        type: 'string',
        minWidth: 110,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        valueGetter: (_value, row) => row.commodityStyle?.name || '',
        getFilterSelectOptions: (client) => getDistinctValueOptions(client, 'Product.commodityStyle'),
      },
      commoditySize: {
        headerName: 'Pack-Style',
        type: 'string',
        minWidth: 100,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        valueGetter: (_value, row) => row.commoditySize?.name || '',
        getFilterSelectOptions: (client) => getDistinctValueOptions(client, 'Product.commoditySize'),
      },
      commodityLabel: {
        headerName: 'Label',
        type: 'string',
        minWidth: 110,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        valueGetter: (_value, row) => row.commodityLabel?.name || '',
        getFilterSelectOptions: (client) => getDistinctValueOptions(client, 'Product.commodityLabel'),
      },
      costBasis: {
        headerName: 'Cost Basis',
        type: 'number',
        minWidth: 120,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        valueGetter: (_value, row) => {
          if (isAutogeneratedRow(row) || isHeaderRow(row)) return '';
          return row.costBasis ? money.toHuman(row.costBasis) : '--';
        },
      },
      countryOfOrigin: {
        headerName: 'COO',
        type: 'string',
        minWidth: 110,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        getFilterSelectOptions: async () =>
          Countries.All.filter((c) => c.code).map((c) => ({ value: c.code, label: `${c.name} (${c.code})` })),
      },
      grade: {
        headerName: 'Grade',
        type: 'string',
        width: 120,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        getFilterSelectOptions: (client) => getDistinctValueOptions(client, 'Product.grade'),
      },
      commodityUnit: {
        headerName: 'Unit Of Measure',
        type: 'string',
        minWidth: 120,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
        valueGetter: (_value, row) => row.commodityUnit?.name || '',
        getFilterSelectOptions: (client) => getDistinctValueOptions(client, 'Product.commodityUnit'),
      },
      organic: {
        headerName: 'Organic',
        type: 'boolean',
        width: 100,
        renderHeader: BoldHeader,
        renderCell: OptionalBooleanRenderCell,
        getFilterSelectOptions: async () => [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      weightPerInventoryUnit: {
        headerName: 'Unit Weight',
        type: 'number',
        minWidth: 100,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
      },
      variableWeight: {
        headerName: 'Variable Weight',
        type: 'boolean',
        minWidth: 100,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalBooleanRenderCell,
      },
      cartonsPerPallet: {
        headerName: 'Units Per Pallet',
        type: 'number',
        minWidth: 100,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
      },
      disabled: {
        headerName: 'Disabled',
        type: 'boolean',
        width: 100,
        renderHeader: BoldHeader,
        renderCell: OptionalBooleanRenderCell,
      },
      gtinCode: {
        headerName: 'GTIN',
        type: 'string',
        minWidth: 100,
        flex: 1,
        renderHeader: BoldHeader,
        renderCell: OptionalRenderCell,
      },
      ...(customFields ?? {}),
      actions: {
        type: 'actions',
        headerName: 'Actions',
        width: 100,
        getActions: (params: GridRowParams<CatalogGridRow>) => {
          // Don't show actions for group rows or empty commodity rows
          if (isAutogeneratedRow(params.row) || isHeaderRow(params.row)) {
            return [];
          }

          return [
            <GridActionsCellItem
              key="edit"
              icon={<RiPencilLine size={20} />}
              label="Edit"
              onClick={() => onEdit?.(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              key="duplicate"
              icon={<RiFileCopyLine size={20} />}
              label="Duplicate"
              onClick={() => onDuplicate?.(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              key="toggle-disabled"
              icon={params.row.disabled ? <RiToggleFill size={20} /> : <RiToggleLine size={20} />}
              label={params.row.disabled ? 'Enable' : 'Disable'}
              onClick={() => onToggleDisabled?.(params.row)}
              showInMenu
            />,
          ];
        },
      },
    }),
    [customFields, onEdit, onDuplicate, onToggleDisabled]
  );
}

export const getRowsWithoutGroups = ({ apiRef }: GridGetRowsToExportParams) => {
  const rows = gridFilteredSortedRowIdsSelector(apiRef);
  const tree = gridRowTreeSelector(apiRef);

  return rows.filter((rowId) => {
    if (tree[rowId]?.type === 'group') return false;
    // Also filter out empty parent placeholder rows
    if (typeof rowId === 'string' && rowId.startsWith(COMMODITY_KEY_PREFIX)) return false;
    return true;
  });
};
